/**
 * Browser binding for the "Sign in with Optro" OAuth round trip.
 *
 * The backend holds the pending sign-in (PKCE verifier, chosen instance) in a
 * shared store keyed by the OAuth `state`. A lookup keyed only by `state` is
 * completable by ANY user agent that learns it, which is login CSRF: an attacker
 * authorizes against their own Optro account and then gets a victim to load the
 * callback URL, silently signing that browser into the attacker's account.
 *
 * So the kickoff also registers the SHA-256 of a nonce that lives only in this
 * tab's `sessionStorage`, and the callback must present the nonce itself. A
 * browser that did not start the flow does not have it.
 */

const STORAGE_KEY = "optro_state_binding";

const toHex = (buffer: ArrayBuffer): string =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

/**
 * Mint a nonce, keep it in `sessionStorage` for the round trip, and return the
 * SHA-256 hex digest to send with the kickoff request.
 */
export const createOptroStateBinding = async (): Promise<string> => {
  const nonce = toHex(crypto.getRandomValues(new Uint8Array(32)).buffer);
  sessionStorage.setItem(STORAGE_KEY, nonce);
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(nonce),
  );
  return toHex(digest);
};

/**
 * Read and clear the nonce this tab stashed at kickoff. Single-use: the state it
 * unlocks is consumed by the callback, so a leftover value could only ever
 * unlock nothing.
 */
export const consumeOptroStateBinding = (): string | null => {
  const nonce = sessionStorage.getItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
  return nonce;
};
